import './applyPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { FormEvent, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroSearching from '../../../assets/hero-searching.svg';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, resume } = useWorkflowStore();

  const {
    computed: { userFirstName },
  } = useUserStore();

  const {
    computed: { canResumeWorkflow },
  } = useApplyStore();

  useEffect((): void => {
    // re-fetch parameters from the URL to support the case where the user was
    // navigated to this page from inside the app and we are using query params
    // to adjust the lead channel code for submission
    useParameterStore
      .getState()
      .setFromURLSearchParams(new URLSearchParams(window.location.search));
  }, []);

  const formSubmit = (event: FormEvent): void => {
    event.preventDefault();

    navigate(canResumeWorkflow ? resume() : next());
  };

  return (
    <div className="apply-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={useWorkflowStore.getState().computed.progress}
            showDrawerToggle
            showLogo
            splitLayout
          />

          <main>
            <figure>
              <img alt={t('imageAlts.heroSearching')} src={heroSearching} />
            </figure>
            <form
              data-testid={locator.pages.apply.form.root}
              noValidate
              onSubmit={formSubmit}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.apply.form.h1}>
                  {t('ApplyPage.title', { firstName: userFirstName })}
                </h1>
                <h2 data-testid={locator.pages.apply.form.h2}>
                  {t('ApplyPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    data-testid={locator.pages.apply.form.submitButton}
                    fullWidth
                    submit
                  >
                    {t(
                      canResumeWorkflow
                        ? 'ApplyPage.resume'
                        : 'ApplyPage.continue',
                    )}
                  </Button>
                </div>
              </div>
              <footer data-testid={locator.pages.apply.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyPage';
